import type { CrmDayPoint, CrmKpis } from '@chatbot/shared-types';
import { TIBO_CHART_PALETTE } from '../../lib/chart-colors.ts';
import { compararConPeriodoAnterior } from '../../lib/comparacion.ts';
import { KpiCard } from './KpiCard.tsx';

function fmtPct(rate: number): string {
  return `${(rate * 100).toFixed(1)}%`;
}

function fmtMinutes(min: number | null): string {
  if (min === null) return '—';
  if (min < 60) return `${min.toFixed(1)} min`;
  const h = Math.floor(min / 60);
  return `${h}h ${Math.round(min - h * 60)}min`;
}

/**
 * Los 6 KPIs estándar de CRM conversacional.
 *
 * Sólo dos llevan comparación contra el período anterior —conversaciones y
 * mensajes— porque son los únicos con serie diaria en `byDay`. Los otros cuatro
 * llegan como agregado del rango completo: para compararlos habría que
 * inventarles una historia que la API no manda. Cuatro tarjetas sin flecha son
 * preferibles a cuatro flechas sin respaldo.
 */
export function CrmKpiGrid({
  kpis,
  byDay = [],
  coverage,
}: {
  kpis: CrmKpis;
  byDay?: ReadonlyArray<CrmDayPoint>;
  /** Desde cuándo hay datos en el pipe. Ver `compararConPeriodoAnterior`. */
  coverage?: { from: string | null; partial: boolean };
}) {
  const conversaciones = compararConPeriodoAnterior(
    byDay.map((d) => ({ date: d.date, valor: d.conversations })),
    coverage,
  );
  const mensajes = compararConPeriodoAnterior(
    byDay.map((d) => ({ date: d.date, valor: d.messages })),
    coverage,
  );

  return (
    <div
      id="metrics-kpis"
      data-testid="metrics-kpis"
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4"
    >
      <KpiCard
        label="Conversaciones nuevas"
        value={kpis.conversationsNew}
        color={TIBO_CHART_PALETTE.primary}
        comparacion={conversaciones}
      />
      <KpiCard
        label="Contactos únicos"
        value={kpis.contactsUnique}
        color={TIBO_CHART_PALETTE.secondary}
      />
      <KpiCard
        label="Mensajes"
        value={kpis.messagesTotal}
        color={TIBO_CHART_PALETTE.ai}
        comparacion={mensajes}
      />
      <KpiCard
        label="Resueltas por el bot"
        value={fmtPct(kpis.botResolutionRate)}
        color={TIBO_CHART_PALETTE.success}
      />
      <KpiCard
        label="Escalamiento"
        value={fmtPct(kpis.escalationRate)}
        color={TIBO_CHART_PALETTE.warning}
        sentidoInverso
      />
      <KpiCard
        label="Duración media"
        value={fmtMinutes(kpis.avgConversationMinutes)}
        color={TIBO_CHART_PALETTE.info}
      />
    </div>
  );
}
